import {
  ASSET_TYPES,
  planSlipApply,
  resolveUnitCost,
  type AssetType,
  type Holding,
  type ParsedSlip,
  type SlipCostBasis,
  type SlipPlan,
  type SlipReview,
} from '@porttang/core';
import { useMemo, useState, type ReactNode } from 'react';
import { formatQuantity } from '../../shared/format';
import { SaleRealizedRow } from '../realized/SaleRealizedRow';
import { useScanSlip, type ScanSlipResult } from './useScanSlip';

const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  stock: 'หุ้น',
  etf: 'ETF',
  crypto: 'คริปโต',
  cash: 'เงินสด',
};

/** เงินสดไม่มีการซื้อขายให้ถัวเฉลี่ย จึงไม่เป็นตัวเลือกบนจอนี้ */
const SLIP_ASSET_TYPES = ASSET_TYPES.filter((type) => type !== 'cash');

const inputClass =
  'w-full rounded-md border border-line-strong bg-sunken px-3 py-2 text-ink-100 outline-none focus:border-brand';

const NEW_PLATFORM = '__new__';

/** ฐานต้นทุนที่เลือกได้บนจอ — `manual` คือผู้ใช้พิมพ์ตัวเลขเอง */
type CostBasisChoice = SlipCostBasis | 'manual';

interface FormValues {
  side: 'buy' | 'sell';
  platform: string;
  customPlatform: string;
  assetType: AssetType;
  symbol: string;
  coingeckoId: string;
  quantity: string;
  unitCost: string;
  currency: string;
  basis: CostBasisChoice;
}

/**
 * เติมค่าเริ่มต้นจากสิ่งที่ OCR อ่านได้ — ช่องที่อ่านไม่ได้เว้นว่างไว้ **ห้ามเดาแทน**
 *
 * ค่าเริ่มต้นของฐานต้นทุนคือ "ยอดสุทธิ ÷ จำนวน" เพราะนั่นคือเงินที่ออกจากกระเป๋าจริง
 * ถ้าสลิปไม่มียอดสุทธิให้ค่อยถอยไปใช้ราคาต่อหน่วย
 */
function initialValues(slip: ParsedSlip, platforms: readonly string[]): FormValues {
  const platform = slip.platform ?? '';
  const known = platforms.some((name) => name.toLowerCase() === platform.toLowerCase());
  const basis: CostBasisChoice = slip.netTotal !== null ? 'net-total' : 'unit-price';

  return {
    side: slip.side ?? 'buy',
    platform: known ? platform : NEW_PLATFORM,
    customPlatform: known ? '' : platform,
    assetType: 'etf',
    symbol: slip.symbol ?? '',
    coingeckoId: '',
    quantity: slip.quantity ?? '',
    unitCost: unitCostFor(slip, basis) ?? '',
    currency: slip.currency ?? '',
    basis,
  };
}

function unitCostFor(slip: ParsedSlip, basis: CostBasisChoice): string | null {
  if (basis === 'manual') {
    return null;
  }
  const result = resolveUnitCost(slip, basis);
  return result.state === 'ok' ? result.unitCost : null;
}

function platformOf(values: FormValues): string {
  return values.platform === NEW_PLATFORM ? values.customPlatform : values.platform;
}

function toReview(values: FormValues): SlipReview {
  return {
    side: values.side,
    platform: platformOf(values),
    assetType: values.assetType,
    symbol: values.symbol,
    quantity: values.quantity,
    unitCost: values.unitCost,
    currency: values.currency,
    coingeckoId: values.coingeckoId,
  };
}

export function SlipScanModal({
  holdings,
  platforms,
  isSaving,
  onCancel,
  onApply,
}: {
  holdings: readonly Holding[];
  platforms: readonly string[];
  isSaving: boolean;
  onCancel: () => void;
  /** เรียกเมื่อผู้ใช้ยืนยันแล้ว — `slipHash` เป็น null เมื่อสลิปอ่านได้ไม่พอจะกันซ้ำ */
  onApply: (plan: SlipPlan, slipHash: string | null) => void;
}) {
  const scan = useScanSlip();
  const [scanned, setScanned] = useState<ScanSlipResult | null>(null);
  const [values, setValues] = useState<FormValues | null>(null);

  function handleFile(file: File | undefined) {
    if (file === undefined) {
      return;
    }
    scan.mutate(file, {
      onSuccess: (result) => {
        setScanned(result);
        setValues(initialValues(result.slip, platforms));
      },
    });
  }

  function patch(changes: Partial<FormValues>) {
    setValues((current) => (current === null ? current : { ...current, ...changes }));
  }

  /** เปลี่ยนฐานต้นทุน = เปลี่ยนตัวเลขในช่องตาม ไม่ใช่แค่ติ๊กเฉย ๆ */
  function chooseBasis(basis: CostBasisChoice) {
    if (scanned === null) {
      return;
    }
    patch({
      basis,
      ...(basis !== 'manual' && { unitCost: unitCostFor(scanned.slip, basis) ?? '' }),
    });
  }

  const plan = useMemo<SlipPlan | null>(
    () => (values === null ? null : planSlipApply(toReview(values), holdings)),
    [values, holdings],
  );

  const isBlocked = scanned?.outOfScope !== null && scanned?.outOfScope !== undefined;
  const canSubmit = plan !== null && plan.kind !== 'blocked' && !isBlocked && !isSaving;

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (plan === null || !canSubmit) {
      return;
    }
    onApply(plan, scanned?.slipHash ?? null);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4">
      <form
        onSubmit={handleSubmit}
        className="my-8 w-full max-w-lg rounded-[14px] border border-line bg-ink p-6"
      >
        <h2 className="text-lg font-semibold text-ink-100">สแกนสลิปซื้อขาย</h2>
        <p className="mt-1 text-xs leading-relaxed text-ink-500">
          ระบบอ่านตัวเลขจากรูปมาเติมให้ แล้วให้ตรวจก่อนบันทึกทุกครั้ง — รูปไม่ถูกเก็บไว้ที่ไหน
        </p>

        {scanned === null ? (
          <PickStage isPending={scan.isPending} error={scan.error} onPick={handleFile} />
        ) : (
          values !== null && (
            <ReviewStage
              slip={scanned.slip}
              scanned={scanned}
              values={values}
              platforms={platforms}
              plan={plan}
              onPatch={patch}
              onChooseBasis={chooseBasis}
            />
          )
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-line-strong px-4 py-2 text-sm text-ink-300 transition hover:text-ink-100"
          >
            ยกเลิก
          </button>
          {scanned !== null && (
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-ink-100 transition hover:brightness-110 disabled:opacity-50"
            >
              {isSaving ? 'กำลังบันทึก…' : 'ยืนยันและบันทึก'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function PickStage({
  isPending,
  error,
  onPick,
}: {
  isPending: boolean;
  error: Error | null;
  onPick: (file: File | undefined) => void;
}) {
  return (
    <div className="mt-5">
      <label className="block cursor-pointer rounded-[12px] border border-dashed border-line-dash px-6 py-10 text-center transition hover:border-brand">
        <input
          type="file"
          accept="image/*"
          className="sr-only"
          disabled={isPending}
          onChange={(event) => onPick(event.target.files?.[0])}
        />
        <span className="block text-sm text-ink-200">
          {isPending ? 'กำลังอ่านสลิป…' : 'เลือกรูปสลิป หรือถ่ายใหม่'}
        </span>
        <span className="mt-1.5 block text-xs text-ink-500">
          รองรับ JPEG, PNG, WebP และ HEIC — รูปถูกย่อก่อนส่งอัตโนมัติ
        </span>
      </label>

      {error !== null && (
        <p
          role="alert"
          className="mt-4 rounded-lg border border-down/25 bg-down/10 px-3.5 py-2.5 text-xs leading-relaxed text-down"
        >
          {error.message}
        </p>
      )}
    </div>
  );
}

function ReviewStage({
  slip,
  scanned,
  values,
  platforms,
  plan,
  onPatch,
  onChooseBasis,
}: {
  slip: ParsedSlip;
  scanned: ScanSlipResult;
  values: FormValues;
  platforms: readonly string[];
  plan: SlipPlan | null;
  onPatch: (changes: Partial<FormValues>) => void;
  onChooseBasis: (basis: CostBasisChoice) => void;
}) {
  const unitPriceCost = unitCostFor(slip, 'unit-price');
  const netTotalCost = unitCostFor(slip, 'net-total');

  return (
    <div className="mt-5 space-y-4">
      {scanned.outOfScope !== null && (
        <Banner tone="down">
          {scanned.outOfScope}
          <span className="mt-1 block text-[11px] opacity-80">
            บันทึกรายการนี้ไม่ได้ เพราะระบบไม่มีแหล่งราคาให้ใช้คำนวณ
          </span>
        </Banner>
      )}

      {scanned.appliedAt !== null && (
        <Banner tone="warn">
          สลิปใบนี้เคยบันทึกเข้าพอร์ตแล้วเมื่อ {formatDateTime(scanned.appliedAt)}
          <span className="mt-1 block text-[11px] opacity-80">
            บันทึกซ้ำจะทำให้จำนวนหน่วยเพิ่มขึ้นทั้งที่ไม่ได้ซื้อเพิ่ม —
            ยืนยันต่อได้ถ้าแน่ใจว่าเป็นคนละรายการ
          </span>
        </Banner>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="ประเภทรายการ"
          hint={slip.side === null ? 'อ่านจากสลิปไม่ได้ — เลือกเอง' : undefined}
        >
          <div className="flex gap-2">
            {(['buy', 'sell'] as const).map((side) => (
              <Choice
                key={side}
                checked={values.side === side}
                onSelect={() => onPatch({ side })}
                label={side === 'buy' ? 'ซื้อ' : 'ขาย'}
              />
            ))}
          </div>
        </Field>

        <Field label="ประเภทสินทรัพย์" hint="สลิปไม่ได้บอกว่าเป็นหุ้นหรือ ETF — เลือกให้ตรง">
          <div className="flex flex-wrap gap-2">
            {SLIP_ASSET_TYPES.map((type) => (
              <Choice
                key={type}
                checked={values.assetType === type}
                onSelect={() => onPatch({ assetType: type })}
                label={ASSET_TYPE_LABELS[type]}
              />
            ))}
          </div>
        </Field>

        <Field label="แพลตฟอร์ม">
          <select
            className={inputClass}
            value={values.platform}
            onChange={(event) => onPatch({ platform: event.target.value })}
          >
            {platforms.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
            <option value={NEW_PLATFORM}>+ เพิ่มแพลตฟอร์มใหม่…</option>
          </select>
          {values.platform === NEW_PLATFORM && (
            <input
              className={`${inputClass} mt-2`}
              value={values.customPlatform}
              placeholder="ชื่อแพลตฟอร์ม"
              onChange={(event) => onPatch({ customPlatform: event.target.value })}
            />
          )}
        </Field>

        <Field
          label="สัญลักษณ์"
          hint={slip.symbol === null ? 'อ่านจากสลิปไม่ได้ — กรอกเอง' : undefined}
        >
          <input
            className={inputClass}
            value={values.symbol}
            onChange={(event) => onPatch({ symbol: event.target.value.toUpperCase() })}
          />
        </Field>

        {values.assetType === 'crypto' && (
          <Field label="รหัสเหรียญ CoinGecko" hint="เช่น bitcoin — ต้องมีถึงจะดึงราคาได้">
            <input
              className={inputClass}
              value={values.coingeckoId}
              onChange={(event) => onPatch({ coingeckoId: event.target.value.toLowerCase() })}
            />
          </Field>
        )}

        <Field
          label="จำนวน"
          hint={slip.quantity === null ? 'อ่านจากสลิปไม่ได้ — กรอกเอง' : undefined}
        >
          <input
            className={inputClass}
            inputMode="decimal"
            value={values.quantity}
            onChange={(event) => onPatch({ quantity: event.target.value })}
          />
        </Field>

        <Field
          label="สกุลเงิน"
          hint={
            slip.currency === null ? 'สลิปมีหลายสกุล — ระบุเองว่าต้นทุนนี้เป็นสกุลไหน' : undefined
          }
        >
          <input
            className={inputClass}
            value={values.currency}
            maxLength={3}
            onChange={(event) => onPatch({ currency: event.target.value.toUpperCase() })}
          />
        </Field>
      </div>

      <Field
        label={values.side === 'sell' ? 'ราคาขายต่อหน่วยที่จะใช้' : 'ต้นทุนต่อหน่วยที่จะใช้'}
        hint="ทั้งสองตัวเลขมาจากสลิปใบเดียวกัน ต่างกันตรงค่าธรรมเนียม"
      >
        <div className="space-y-2">
          <BasisOption
            checked={values.basis === 'net-total'}
            disabled={netTotalCost === null}
            onSelect={() => onChooseBasis('net-total')}
            title="ยอดสุทธิ ÷ จำนวน"
            value={netTotalCost}
            currency={values.currency}
            note={
              values.side === 'sell'
                ? 'เงินที่ได้รับจริง หักค่าธรรมเนียมแล้ว'
                : 'เงินที่ออกจากกระเป๋าจริง รวมค่าธรรมเนียมแล้ว'
            }
          />
          <BasisOption
            checked={values.basis === 'unit-price'}
            disabled={unitPriceCost === null}
            onSelect={() => onChooseBasis('unit-price')}
            title="ราคาต่อหน่วยตามสลิป"
            value={unitPriceCost}
            currency={values.currency}
            note="ตรงกับตัวเลขที่เห็นในแอปโบรก ไม่นับค่าธรรมเนียม"
          />
        </div>

        <input
          className={`${inputClass} mt-2`}
          inputMode="decimal"
          value={values.unitCost}
          onChange={(event) => onPatch({ unitCost: event.target.value, basis: 'manual' })}
        />
        {slip.fee !== null && (
          <span className="mt-1 block text-xs text-ink-500">
            ค่าธรรมเนียมบนสลิป {slip.fee} {values.currency}
          </span>
        )}
      </Field>

      {plan !== null && <PlanPreview plan={plan} currency={values.currency} />}
    </div>
  );
}

/** สิ่งที่จะเกิดกับพอร์ต — ตัวเลขทั้งหมดคำนวณโดย `@porttang/core` ที่นี่แค่แสดง */
function PlanPreview({ plan, currency }: { plan: SlipPlan; currency: string }) {
  if (plan.kind === 'blocked') {
    return <Banner tone="down">{plan.message}</Banner>;
  }

  if (plan.kind === 'create') {
    return (
      <div className="rounded-lg border border-line-strong bg-sunken p-4">
        <div className="pixel-label">รายการใหม่</div>
        <p className="mt-3 text-sm text-ink-100">
          เพิ่ม {plan.holding.symbol} ที่ {plan.holding.platform} จำนวน{' '}
          {formatQuantity(plan.holding.quantity)} หน่วย ต้นทุน {plan.holding.avgCost}{' '}
          {plan.holding.currency}
        </p>
        <p className="mt-1.5 text-xs text-ink-500">ยังไม่มีสัญลักษณ์นี้ที่แพลตฟอร์มนี้ในพอร์ต</p>
      </div>
    );
  }

  const { holding, next } = plan;

  return (
    <div className="rounded-lg border border-line-strong bg-sunken p-4">
      <div className="pixel-label">ก่อน → หลัง</div>

      {next === null ? (
        <p className="mt-3 text-sm leading-relaxed text-warn">
          ขายหมดพอดี — {holding.symbol} จะถูกลบออกจากพอร์ต
        </p>
      ) : (
        <dl className="mt-3 space-y-2.5 text-sm">
          <PreviewRow
            label="จำนวน"
            before={formatQuantity(holding.quantity)}
            after={formatQuantity(next.quantity)}
          />
          <PreviewRow
            label="ต้นทุน/หน่วย"
            before={`${holding.avgCost} ${holding.currency}`}
            after={`${next.avgCost} ${currency}`}
            note={plan.kind === 'reduce' ? 'ขายแล้วต้นทุนเฉลี่ยไม่เปลี่ยน' : undefined}
          />
        </dl>
      )}

      {plan.kind === 'reduce' && <SaleRealizedRow sale={plan.sale} />}
    </div>
  );
}

function BasisOption({
  checked,
  disabled,
  onSelect,
  title,
  value,
  currency,
  note,
}: {
  checked: boolean;
  disabled: boolean;
  onSelect: () => void;
  title: string;
  value: string | null;
  currency: string;
  note: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={[
        'block w-full rounded-lg border px-3.5 py-2.5 text-left transition disabled:opacity-40',
        checked ? 'border-brand bg-brand-soft' : 'border-line-strong hover:border-line-dash',
      ].join(' ')}
    >
      <span className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm text-ink-100">
        <span>{title}</span>
        <span className="font-mono text-[13px]">
          {value === null ? 'อ่านไม่ได้' : `${value} ${currency}`}
        </span>
      </span>
      <span className="mt-0.5 block text-[11px] text-ink-500">{note}</span>
    </button>
  );
}

function Choice({
  checked,
  onSelect,
  label,
}: {
  checked: boolean;
  onSelect: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        'rounded-md border px-3 py-1.5 text-sm transition',
        checked
          ? 'border-brand bg-brand-soft text-brand-text'
          : 'border-line-strong text-ink-300 hover:text-ink-100',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string | undefined;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm text-ink-200">{label}</span>
      {children}
      {hint !== undefined && <span className="mt-1 block text-xs text-warn">{hint}</span>}
    </label>
  );
}

function Banner({ tone, children }: { tone: 'down' | 'warn'; children: ReactNode }) {
  const toneClass =
    tone === 'down' ? 'border-down/25 bg-down/10 text-down' : 'border-warn/25 bg-warn/10 text-warn';

  return (
    <p
      role="alert"
      className={`rounded-lg border px-3.5 py-2.5 text-xs leading-relaxed ${toneClass}`}
    >
      {children}
    </p>
  );
}

function PreviewRow({
  label,
  before,
  after,
  note,
}: {
  label: string;
  before: string;
  after: string;
  note?: string | undefined;
}) {
  return (
    <div>
      <dt className="text-xs text-ink-400">{label}</dt>
      <dd className="mt-0.5 flex flex-wrap items-baseline gap-x-2 font-mono text-[13px]">
        <span className="text-ink-500 line-through">{before}</span>
        <span aria-hidden className="text-ink-600">
          →
        </span>
        <span className={before === after ? 'text-ink-400' : 'text-ink-100'}>{after}</span>
      </dd>
      {note !== undefined && <p className="mt-0.5 text-[11px] text-ink-600">{note}</p>}
    </div>
  );
}

/** เวลาในระบบเป็น UTC เสมอ แปลงเป็นเวลาไทยเฉพาะตอนแสดงผล */
function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}
